import React from 'react';
import { FaTimes, FaList } from 'react-icons/fa';

interface Question {
  questionId: string;
  questionOrder?: number;
  questionNumber?: number;
}

interface Course {
  courseId: string;
  courseName?: string;
  questions?: Question[];
}

interface Group {
  groupId: string;
  groupName?: string;
  courses?: Course[];
}

interface QuestionListPanelProps {
  questions: any[];
  currentQuestionIndex: number;
  onQuestionNavigate: (index: number) => void;
  onClose: () => void;
  groups?: Group[];
}

const QuestionListPanel: React.FC<QuestionListPanelProps> = ({
  questions,
  currentQuestionIndex,
  onQuestionNavigate,
  onClose,
  groups
}) => {
  return (
    <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h2 className="text-lg font-bold text-gray-800">Soru Listesi</h2>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <FaTimes className="text-gray-500" />
          </button>
        </div>

        {/* Question Grid */}
        <div className="p-4 overflow-y-auto max-h-[60vh]">
          {groups && groups.length > 0 ? (
            <div className="space-y-6">
              {groups.map((group, groupIndex) => (
                <div key={group.groupId} className="space-y-3">
                  {/* Group Name */}
                  <h3 className="text-sm font-semibold text-gray-600 uppercase tracking-wide">
                    {group.groupName || `Grup ${groupIndex + 1}`}
                  </h3>
                  
                  {/* Questions Grid for all courses in the group */}
                  <div className="grid grid-cols-8 gap-2">
                    {(() => {
                      // Collect all questions from all courses in this group
                      const groupQuestions: any[] = [];
                      group.courses?.forEach(course => {
                        course.questions?.forEach(question => {
                          groupQuestions.push(question);
                        });
                      });
                      
                      return groupQuestions.map((question, questionIndex) => {
                        // Find global index for this question
                        let globalIndex = -1;
                        for (let i = 0; i < questions.length; i++) {
                          if (questions[i].questionId === question.questionId) {
                            globalIndex = i;
                            break;
                          }
                        }
                        
                        const isCurrentQuestion = globalIndex === currentQuestionIndex;
                        const localQuestionNumber = questionIndex + 1;
                        
                        return (
                          <button
                            key={question.questionId}
                            onClick={() => {
                              if (globalIndex >= 0) {
                                onQuestionNavigate(globalIndex);
                                onClose();
                              }
                            }}
                            className={`aspect-square rounded-lg border-2 text-sm font-semibold transition-all ${
                              isCurrentQuestion
                                ? 'bg-blue-500 text-white border-blue-600 shadow-lg scale-110'
                                : 'bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-100 hover:border-gray-300 hover:scale-105 active:scale-95'
                            }`}
                            disabled={globalIndex < 0}
                          >
                            {localQuestionNumber}
                          </button>
                        );
                      });
                    })()}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            // Fallback to simple grid if no groups
            <div className="grid grid-cols-8 gap-2">
              {questions.map((question, index) => {
                const isCurrentQuestion = index === currentQuestionIndex;
                return (
                  <button
                    key={question.questionId || index}
                    onClick={() => {
                      onQuestionNavigate(index);
                      onClose();
                    }}
                    className={`aspect-square rounded-lg border-2 text-sm font-semibold transition-all ${
                      isCurrentQuestion
                        ? 'bg-blue-500 text-white border-blue-600 shadow-lg scale-110'
                        : 'bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-100 hover:border-gray-300 hover:scale-105 active:scale-95'
                    }`}
                  >
                    {index + 1}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuestionListPanel;